import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { mergeMap, retryWhen, timeout } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subscriber, throwError, timer } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class HttpService {
    public localStorageTokenKey = 'token';

    constructor(
        @Inject(PLATFORM_ID) protected platformId: Object,
        protected http: HttpClient,
        protected router: Router
    ) {}

    // ใช้กับ GET เท่านั้น: ลองซ้ำอีก 2 ครั้ง (เว้นครั้งละ 1 วิ) เฉพาะ error ชั่วคราว
    // (เน็ตหลุด/timeout/5xx) — ไม่ retry 4xx เพราะยิงซ้ำยังไงก็ไม่หาย
    // งานเขียน (POST/PUT/DELETE) ห้าม retry: ถ้าครั้งแรกสำเร็จแต่คำตอบกลับไม่ถึง browser
    // การยิงซ้ำ = สร้างข้อมูล/ส่งเมลเบิ้ล (เดิมเป็น retry(5) ทุก method — user เคาะแก้ 4 ก.ย. 2569)
    private retryTransientErrors() {
        return retryWhen<any>((errors) =>
            errors.pipe(
                mergeMap((error, attempt) => {
                    const status = error && error.status;
                    const isClientError = status >= 400 && status < 500;
                    if (attempt >= 2 || isClientError) {
                        return throwError(error);
                    }
                    return timer(1000);
                })
            )
        );
    }

    // get
    public get(url: string, hasAuthorization: boolean = true): Observable<any> {
        return new Observable((observer) => {
            this.getRequestOptions(hasAuthorization).subscribe((headers) => {
                this.http
                    .get(url, headers)
                    .pipe(timeout(30000), this.retryTransientErrors())
                    .subscribe(
                        (result: any) => {
                            if (result.status === 'error') {
                                this.redirectToErrorPage(observer, result);
                            } else {
                                observer.next(result);
                                observer.complete();
                            }
                        },
                        (response) => observer.next(response.error)
                    );
            }, this.onCannotCreateRequestHeader);
        });
    }

    // post
    public post(url: string, body: any, hasAuthorization: boolean = true): Observable<any> {
        return new Observable((observer) => {
            this.getRequestOptions(hasAuthorization).subscribe((headers) => {
                this.http
                    .post(url, body, headers)
                    .subscribe(
                        (result: any) => {
                            if (result.status === 'error') {
                                this.redirectToErrorPage(observer, result);
                            } else {
                                observer.next(result);
                            }
                        },
                        (response) => observer.next(response.error)
                    );
            }, this.onCannotCreateRequestHeader);
        });
    }

    // put
    public put(url: string, body: any, hasAuthorization: boolean = true): Observable<any> {
        return new Observable((observer) => {
            this.getRequestOptions(hasAuthorization).subscribe((headers) => {
                this.http
                    .put(url, body, headers)
                    .subscribe(
                        (result: any) => {
                            if (result.status === 'error') {
                                this.redirectToErrorPage(observer, result);
                            } else {
                                observer.next(result);
                            }
                        },
                        (response) => observer.next(response.error)
                    );
            }, this.onCannotCreateRequestHeader);
        });
    }

    // delete
    public delete(url: string, hasAuthorization: boolean = true, body?: any): Observable<any> {
        return new Observable((observer) => {
            this.getRequestOptions(hasAuthorization).subscribe((headers) => {
                this.http
                    .delete(url, { ...headers, body })
                    .subscribe(
                        (result: any) => {
                            if (result.status === 'error') {
                                this.redirectToErrorPage(observer, result);
                            } else {
                                observer.next(result);
                            }
                        },
                        (response) => observer.next(response.error)
                    );
            }, this.onCannotCreateRequestHeader);
        });
    }

    private redirectToErrorPage(observer: any, result: any) {
        switch (result.error.code) {
            case 401:
                this.router.navigate(['401']);
                break;
            case 404:
                this.router.navigate(['404']);
                break;
            case 500:
                this.router.navigate(['505']);
                break;
            default:
                observer.next(result);
        }
    }

    // custom request
    public request(request: Request, headers: any): Observable<any> {
        headers.responseType = 'json';
        return this.http.request(request.method.toString(), request.url, headers);
    }

    public getRequestOptions(hasAuthorization: boolean): Observable<any> {
        return new Observable((observer) => {
            const httpHeaders = new HttpHeaders({
                Accept: 'application/json',
                'Content-Type': 'application/json',
            });

            const requestOptions = {
                headers: httpHeaders,
                responseType: 'json',
            };

            if (!hasAuthorization) {
                observer.next(requestOptions);
            } else {
                requestOptions.headers = requestOptions.headers.set('Authorization', this.getToken());
                observer.next(requestOptions);
            }
        });
    }

    private onCannotCreateRequestHeader(reason: any): Observable<any> {
        alert('something went wrong');
        return Observable.throw(reason);
    }

    public getToken(): string {
        let token: string | null;
        token = localStorage.getItem(this.localStorageTokenKey);

        if (token) {
            return token;
        }

        return '';
    }

    public getQueryString(obj: any): string {
        const params = new URLSearchParams();
        for (const key in obj) {
            let param = obj[key];
            if (typeof param === 'object') {
                param = JSON.stringify(param);
            }
            params.set(key, param);
        }
        return params.toString();
    }
}
